import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { DraftProvider, useDraft } from '../contexts/DraftContext';
import DraftBoard from '../components/DraftBoard';
import { HeroData } from '../models/HeroData';
import {
  NavBar, Logo, LoginButton, PageContainer, Section, ContentColumn, DemoColumn,
  FeatureTitle, FeatureDescription, Highlight, OriginStory, NarrationSection,
  NarrationQuote, VisualIcon, DemoContainer, CTASection, HeroSection, CTAButton,
  useScrollStory
} from './marketing/MarketingShared';

// Wrapper component that auto-adds a second scenario for delete demo
const DeleteScenarioDemo = ({ isDisabled }) => {
  const { scenariosFirstPick, addScenario } = useDraft();
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    // Add second scenario only once when component mounts
    if (!initialized && scenariosFirstPick.length === 1) {
      addScenario('first');
      setInitialized(true);
    }
  }, [initialized, scenariosFirstPick.length, addScenario]);

  return (
    <DraftBoard
      showButtons={{ addScenario: true, delete: true }}
      isMarketingDemo={true}
      isDisabledDemo={isDisabled}
      maxScenariosDemo={3}
    />
  );
};

const heroPick = (key) => ({ hero: HeroData[key], isBanned: false });

// Fills the main board's picks one at a time, at a natural pace, to represent
// the board syncing live as picks happen in your actual match — never clears
// an already-synced pick (a real GSI feed only ever adds to the draft, it
// doesn't undo a hero once it's been picked in-game).
const GsiSyncDemo = ({ isActive }) => {
  const { scenariosFirstPick, updateScenario } = useDraft();
  const heroOrder = useRef(['pudge', 'invoker', 'crystal_maiden', 'axe', 'lina', 'earthshaker', 'juggernaut', 'phantom_assassin']);
  const runRef = useRef(false);
  // Interval callbacks otherwise close over the scenariosFirstPick snapshot from
  // when the effect was created, rebuilding picks from that stale (emptier) copy
  // each tick and erasing whatever a previous tick had just synced in. Reading
  // through a ref that's updated every render keeps each tick working off the
  // latest picks.
  const scenariosRef = useRef(scenariosFirstPick);
  useEffect(() => { scenariosRef.current = scenariosFirstPick; }, [scenariosFirstPick]);

  useEffect(() => {
    if (!isActive) {
      runRef.current = false;
      return undefined;
    }
    if (runRef.current) return undefined;
    runRef.current = true;
    let step = 0;
    const interval = setInterval(() => {
      const scenario = scenariosRef.current[0];
      const heroes = heroOrder.current;
      if (!scenario || step >= heroes.length) {
        clearInterval(interval);
        return;
      }
      // Only ever fill an empty slot — a pick that's already synced stays put.
      if (!scenario.picks[step]) {
        const picks = [...scenario.picks];
        picks[step] = heroPick(heroes[step]);
        updateScenario('first', scenario.id, { picks });
      }
      step += 1;
    }, 900);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]);

  return (
    <DraftBoard
      showButtons={{}}
      // The 15-slot forehead row (the actual GSI UI) only renders when
      // isMarketingDemo is false — kept visible here since it's part of the
      // real feature, but it's left static rather than auto-animated.
      isMarketingDemo={false}
      isDisabledDemo={!isActive}
    />
  );
};

// Seeds a labelled lane-bracket annotation shortly after the section becomes
// visible, so visitors watch it "draw" onto the board rather than see it static.
const LaneBracketDemo = ({ isActive }) => {
  const { scenariosFirstPick, updateScenario } = useDraft();
  const seededRef = useRef(false);

  useEffect(() => {
    if (!isActive) {
      seededRef.current = false;
      return undefined;
    }
    if (seededRef.current) return undefined;
    const scenario = scenariosFirstPick[0];
    if (!scenario) return undefined;
    const timer = setTimeout(() => {
      seededRef.current = true;
      const picks = [...scenario.picks];
      picks[0] = heroPick('earthshaker');
      picks[4] = heroPick('axe');
      updateScenario('first', scenario.id, {
        picks,
        annotations: [
          { id: 'demo-bracket', side: 'left', fromPos: 1, toPos: 5, label: 'Mid-Off Flex' }
        ]
      });
    }, 700);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]);

  return (
    <DraftBoard
      showButtons={{}}
      isMarketingDemo={true}
      isDisabledDemo={!isActive}
    />
  );
};

// Pre-fills a few picks and turns the neon highlight on immediately, so
// visitors see the "flagged" state rather than an empty board.
const NeonHighlightDemo = ({ isActive }) => {
  const { scenariosFirstPick, updateScenario } = useDraft();
  const seededRef = useRef(false);

  useEffect(() => {
    if (seededRef.current) return;
    const scenario = scenariosFirstPick[0];
    if (!scenario) return;
    seededRef.current = true;
    const picks = [...scenario.picks];
    picks[0] = heroPick('lina');
    picks[1] = heroPick('juggernaut');
    picks[4] = heroPick('crystal_maiden');
    updateScenario('first', scenario.id, { picks, neonHighlight: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <DraftBoard
      showButtons={{}}
      isMarketingDemo={true}
      isDisabledDemo={!isActive}
    />
  );
};

// One board of the Pull Match Data demo — animates its picks filling in over
// time (staggered by `startDelay`) to simulate a match's draft being pulled in.
// A "pull" fetches a whole match's draft at once, so all 24 picks land in a
// single burst — unlike GSI sync, which trickles picks in one at a time as
// they happen live.
const PULL_MATCH_HEROES = [
  'axe', 'lina', 'crystal_maiden', 'juggernaut', 'pudge', 'invoker',
  'earthshaker', 'phantom_assassin', 'sniper', 'windrunner', 'antimage', 'alchemist',
  'axe', 'lina', 'crystal_maiden', 'juggernaut', 'pudge', 'invoker',
  'earthshaker', 'phantom_assassin', 'sniper', 'windrunner', 'antimage', 'alchemist'
];

const PullMatchBoardDemo = ({ isActive, startDelay = 0 }) => {
  const { scenariosFirstPick, updateScenario } = useDraft();
  const runRef = useRef(false);
  // See GsiSyncDemo above — read through a ref so this doesn't act on a stale
  // snapshot of scenariosFirstPick from when the effect started.
  const scenariosRef = useRef(scenariosFirstPick);
  useEffect(() => { scenariosRef.current = scenariosFirstPick; }, [scenariosFirstPick]);

  useEffect(() => {
    if (!isActive) {
      runRef.current = false;
      return undefined;
    }
    if (runRef.current) return undefined;
    runRef.current = true;
    const startTimer = setTimeout(() => {
      const scenario = scenariosRef.current[0];
      if (!scenario) return;
      // Only fill slots that aren't already synced — a pulled hero never gets
      // overwritten or cleared by a later pull.
      const picks = scenario.picks.map((existing, i) => existing || heroPick(PULL_MATCH_HEROES[i]));
      updateScenario('first', scenario.id, { picks });
    }, startDelay);
    return () => clearTimeout(startTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]);

  return (
    <DraftBoard
      showButtons={{}}
      isMarketingDemo={true}
      isDisabledDemo={!isActive}
    />
  );
};

const BentoSection = styled.section`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  padding: 100px 64px 40px;
  scroll-snap-align: start;
  background: #121212;
  position: relative;

  @media (max-width: 968px) {
    padding: 90px 20px 30px;
  }
`;

const BentoHeader = styled.div`
  display: flex;
  align-items: baseline;
  gap: 14px;
  margin-bottom: 24px;
  flex-wrap: wrap;
`;

const BentoEyebrow = styled.span`
  font-size: clamp(14px, 2vw, 20px);
  font-weight: 800;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: #e94560;
`;

const BentoSubtitle = styled.span`
  font-size: 16px;
  color: rgba(255, 255, 255, 0.5);
`;

const BentoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 16px;

  @media (max-width: 968px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const BentoTile = styled.div`
  grid-column: span ${props => props.$span || 2};
  background: linear-gradient(135deg, #1a1a2e 0%, #16161f 100%);
  border: 1px solid rgba(102, 126, 234, 0.18);
  border-radius: 16px;
  padding: 22px;
  display: flex;
  flex-direction: column;
  min-height: 200px;
  transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.3s ease, border-color 0.3s ease;

  &:hover {
    transform: translateY(-4px) scale(1.02);
    border-color: rgba(102, 126, 234, 0.5);
    box-shadow: 0 16px 40px rgba(102, 126, 234, 0.25);
  }

  @media (max-width: 968px) {
    grid-column: span 2;
    min-height: 160px;
  }
`;

const BentoTag = styled.span`
  display: inline-flex;
  align-self: flex-start;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  background: rgba(102, 126, 234, 0.12);
  color: #8b9dfc;
  border: 1px solid rgba(102, 126, 234, 0.3);
  border-radius: 99px;
  padding: 3px 10px;
  margin-bottom: 10px;
`;

const BentoTileTitle = styled.h3`
  font-size: 17px;
  font-weight: 800;
  color: #fff;
  margin: 0 0 6px 0;
`;

const BentoTileDesc = styled.p`
  font-size: 12.5px;
  color: rgba(255, 255, 255, 0.55);
  line-height: 1.55;
  margin: 0;
`;

const BentoVisual = styled.div`
  margin-top: auto;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const BentoPill = styled.span`
  font-size: 10px;
  font-weight: 700;
  padding: 4px 10px;
  border-radius: 99px;
  border: 1px solid;
  ${props => props.$variant === 'us' && `color: #92ff92; border-color: rgba(146,255,146,.35); background: rgba(146,255,146,.08);`}
  ${props => props.$variant === 'enemy' && `color: #ff6b6b; border-color: rgba(255,107,107,.35); background: rgba(255,107,107,.08);`}
  ${props => props.$variant === 'accent' && `color: #8b9dfc; border-color: rgba(102,126,234,.4); background: rgba(102,126,234,.1);`}
`;

const MiniBoardRow = styled.div`
  display: flex;
  gap: 6px;
  width: 100%;
  flex: 1;
  min-height: 0;
`;

/* Clips a real, scaled-down <DraftBoard> to a short preview window (bans + top
   picks) with a fade-out at the bottom, instead of shrinking the whole 24-slot
   board down to illegible size. */
const BentoBoardClip = styled.div`
  flex: 1;
  min-width: 0;
  height: ${props => props.$height || 150}px;
  border-radius: 8px;
  overflow: hidden;
  position: relative;
  background: linear-gradient(to bottom, #202235, #14141d);
  border: 1px solid rgba(255, 255, 255, 0.08);

  &::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 40%;
    background: linear-gradient(to bottom, transparent, #14141d);
    pointer-events: none;
  }

  & > div {
    zoom: ${props => props.$zoom || 0.32};
    pointer-events: none;
  }
`;

const GameLabel = styled.div`
  text-align: center;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #8b9dfc;
  margin-bottom: 8px;
`;

const GameColumn = styled.div`
  display: flex;
  flex-direction: column;
`;

const MarketingLandingPageV3 = () => {
  const navigate = useNavigate();
  const { disableSnap, visibleSection } = useScrollStory('feature1');

  return (
    <PageContainer $disableSnap={disableSnap}>
      <NavBar>
        <Logo />
        <LoginButton onClick={() => navigate('/login')}>
          Sign In
        </LoginButton>
      </NavBar>

        {/* Hero Section */}
        <HeroSection>
          <FeatureTitle style={{ textAlign: 'center' }}>Master Your Dota 2 Draft</FeatureTitle>
          <OriginStory style={{ textAlign: 'center', borderLeft: 'none', paddingLeft: 0, maxWidth: '800px' }}>
            When you're drifting with your thoughts, thinking about the draft yourself...
            Don't you wonder? You need a tool that is the closest thing you can get to your in-game draft boxes.
          </OriginStory>
          <FeatureDescription style={{ textAlign: 'center' }}>
            Plan competitive drafts with the fastest, most intuitive draft planner.
            Compare scenarios, explore alternatives, and perfect your strategy.
          </FeatureDescription>
          <CTAButton onClick={() => navigate('/login')} style={{ marginTop: '24px' }}>
            Get Started Free
          </CTAButton>
        </HeroSection>

        {/* Narration 1: Add Heroes */}
        <NarrationSection>
          <VisualIcon>
            <svg viewBox="0 0 200 200" width="180" height="180">
              <circle cx="100" cy="100" r="80" fill="none" stroke="#667eea" strokeWidth="4"/>
              <path d="M100 60 L100 140 M60 100 L140 100" stroke="#667eea" strokeWidth="6" strokeLinecap="round"/>
            </svg>
          </VisualIcon>
          <NarrationQuote>
            Don't you wonder you need a tool, when you are laying out drafts on how to face the enemy?
          </NarrationQuote>
        </NarrationSection>

        {/* Feature 1: Add Heroes */}
        <Section data-section-id="feature1">
          <ContentColumn data-animate="content">
            <FeatureTitle>Click to Add Heroes</FeatureTitle>
            <FeatureDescription>
              Click any pick slot to open the hero selector.
              <Highlight> Type to search</Highlight> or browse heroes visually.
              Start building your draft one hero at a time.
            </FeatureDescription>
          </ContentColumn>
          <DemoColumn data-animate="demo">
            <DemoContainer>
              <DraftProvider>
                <DraftBoard
                  scenarioId={1}
                  scenarioName="Scenario 1"
                  picks={Array(20).fill(null)}
                  showButtons={{}}
                  isMarketingDemo={true}
                  isDisabledDemo={visibleSection !== 'feature1'}
                />
              </DraftProvider>
            </DemoContainer>
          </DemoColumn>
        </Section>

        {/* Narration 2: Add Scenarios */}
        <NarrationSection>
          <VisualIcon>
            <svg viewBox="0 0 200 200" width="180" height="180">
              <rect x="30" y="30" width="60" height="60" fill="none" stroke="#667eea" strokeWidth="4" rx="8"/>
              <rect x="110" y="30" width="60" height="60" fill="none" stroke="#667eea" strokeWidth="4" rx="8"/>
              <rect x="30" y="110" width="60" height="60" fill="none" stroke="#667eea" strokeWidth="4" rx="8"/>
              <rect x="110" y="110" width="60" height="60" fill="none" stroke="#667eea" strokeWidth="4" rx="8"/>
            </svg>
          </VisualIcon>
          <NarrationQuote>
            Don't you wonder, when laying out what to pick 1st, what got opened, many what ifs, and you got to the 3rd what if... you forgot the first what if. Well, no longer.
          </NarrationQuote>
        </NarrationSection>

        {/* Feature 2: Add Scenario */}
        <Section $reverse data-section-id="feature2">
          <ContentColumn data-animate="content">
            <FeatureTitle>Add Scenarios</FeatureTitle>
            <FeatureDescription>
              Click <Highlight>"+ Add Scenario"</Highlight> to create multiple draft variations.
              Compare first pick vs second pick strategies side-by-side.
            </FeatureDescription>
          </ContentColumn>
          <DemoColumn data-animate="demo">
            <DemoContainer>
              <DraftProvider>
                <DraftBoard
                  scenarioId={2}
                  scenarioName="Scenario 1"
                  picks={Array(20).fill(null)}
                  showButtons={{ addScenario: true }}
                  isMarketingDemo={true}
                  isDisabledDemo={visibleSection !== 'feature2'}
                  maxScenariosDemo={3}
                />
              </DraftProvider>
            </DemoContainer>
          </DemoColumn>
        </Section>

        {/* Narration 3: Delete Scenarios */}
        <NarrationSection>
          <VisualIcon>
            <svg viewBox="0 0 200 200" width="180" height="180">
              <path d="M50 80 L150 80 L140 170 L60 170 Z" fill="none" stroke="#667eea" strokeWidth="4"/>
              <path d="M40 60 L160 60" stroke="#667eea" strokeWidth="4" strokeLinecap="round"/>
              <path d="M80 40 L120 40" stroke="#667eea" strokeWidth="4" strokeLinecap="round"/>
              <path d="M80 100 L80 150 M100 100 L100 150 M120 100 L120 150" stroke="#667eea" strokeWidth="3" strokeLinecap="round"/>
            </svg>
          </VisualIcon>
          <NarrationQuote>
            What if I no longer needed this scenario?
          </NarrationQuote>
        </NarrationSection>

        {/* Feature 3: Delete Scenario */}
        <Section data-section-id="feature3">
          <ContentColumn data-animate="content">
            <FeatureTitle>Delete Scenarios</FeatureTitle>
            <FeatureDescription>
              Click <Highlight>"Delete"</Highlight> to remove scenarios you don't need.
              Keep your workspace clean and focused.
            </FeatureDescription>
          </ContentColumn>
          <DemoColumn data-animate="demo">
            <DemoContainer>
              <DraftProvider>
                <DeleteScenarioDemo isDisabled={visibleSection !== 'feature3'} />
              </DraftProvider>
            </DemoContainer>
          </DemoColumn>
        </Section>

        {/* Narration 4: Duplicate Scenarios */}
        <NarrationSection>
          <VisualIcon>
            <svg viewBox="0 0 200 200" width="180" height="180">
              <rect x="40" y="60" width="80" height="100" fill="none" stroke="#667eea" strokeWidth="4" rx="8"/>
              <rect x="80" y="40" width="80" height="100" fill="none" stroke="#667eea" strokeWidth="4" rx="8"/>
              <path d="M60 85 L100 85 M60 100 L90 100 M60 115 L100 115" stroke="#667eea" strokeWidth="3" strokeLinecap="round"/>
              <path d="M100 65 L140 65 M100 80 L130 80 M100 95 L140 95" stroke="#667eea" strokeWidth="3" strokeLinecap="round"/>
            </svg>
          </VisualIcon>
          <NarrationQuote>
            What if similar scenario like this... but they did this?
          </NarrationQuote>
        </NarrationSection>

        {/* Feature 4: Duplicate Scenario */}
        <Section $reverse data-section-id="feature4">
          <ContentColumn data-animate="content">
            <FeatureTitle>Duplicate Scenarios</FeatureTitle>
            <FeatureDescription>
              Click <Highlight>"Duplicate"</Highlight> to copy an entire scenario.
              Perfect for exploring alternative hero picks without losing your original draft.
            </FeatureDescription>
          </ContentColumn>
          <DemoColumn data-animate="demo">
            <DemoContainer>
              <DraftProvider>
                <DraftBoard
                  scenarioId={5}
                  scenarioName="Original"
                  picks={Array(20).fill(null)}
                  showButtons={{ addScenario: true, delete: true, duplicate: true }}
                  isMarketingDemo={true}
                  isDisabledDemo={visibleSection !== 'feature4'}
                  maxScenariosDemo={3}
                />
              </DraftProvider>
            </DemoContainer>
          </DemoColumn>
        </Section>

        {/* Narration 5: Clear All */}
        <NarrationSection>
          <VisualIcon>
            <svg viewBox="0 0 200 200" width="180" height="180">
              <circle cx="100" cy="100" r="70" fill="none" stroke="#667eea" strokeWidth="4"/>
              <path d="M100 40 L100 100 L140 100" stroke="#667eea" strokeWidth="4" strokeLinecap="round"/>
              <path d="M160 70 L140 50 L140 90" fill="#667eea"/>
            </svg>
          </VisualIcon>
          <NarrationQuote>
            What if you wanna start fresh?
          </NarrationQuote>
        </NarrationSection>

        {/* Feature 5: Clear All */}
        <Section data-section-id="feature5">
          <ContentColumn data-animate="content">
            <FeatureTitle>Clear All Picks</FeatureTitle>
            <FeatureDescription>
              Click <Highlight>"Clear All"</Highlight> to reset all hero picks in a scenario.
              Start fresh while keeping your scenario structure.
            </FeatureDescription>
          </ContentColumn>
          <DemoColumn data-animate="demo">
            <DemoContainer>
              <DraftProvider>
                <DraftBoard
                  scenarioId={7}
                  scenarioName="Scenario to Clear"
                  picks={Array(20).fill(null)}
                  showButtons={{ addScenario: true, delete: true, duplicate: true, clearAll: true }}
                  isMarketingDemo={true}
                  isDisabledDemo={visibleSection !== 'feature5'}
                  maxScenariosDemo={3}
                />
              </DraftProvider>
            </DemoContainer>
          </DemoColumn>
        </Section>

        {/* Narration 6: Spreadsheet Navigation */}
        <NarrationSection>
          <VisualIcon>
            <svg viewBox="0 0 200 200" width="180" height="180">
              <rect x="30" y="60" width="140" height="30" fill="none" stroke="#667eea" strokeWidth="3" rx="4"/>
              <rect x="30" y="90" width="140" height="30" fill="none" stroke="#667eea" strokeWidth="3" rx="4"/>
              <rect x="30" y="120" width="140" height="30" fill="none" stroke="#667eea" strokeWidth="3" rx="4"/>
              <path d="M100 45 L100 30 L90 40 M100 30 L110 40" stroke="#667eea" strokeWidth="3" strokeLinecap="round"/>
              <path d="M55 105 L40 105 L50 95 M40 105 L50 115" stroke="#667eea" strokeWidth="3" strokeLinecap="round"/>
            </svg>
          </VisualIcon>
          <NarrationQuote>
            Oh, I don't wanna move my mouse... What if the app works like an Excel spreadsheet? Say no more.
          </NarrationQuote>
        </NarrationSection>

        {/* Feature 6: Keyboard Navigation (Spreadsheet-like) */}
        <Section $reverse data-section-id="feature6">
          <ContentColumn data-animate="content">
            <FeatureTitle>Work Like a Spreadsheet</FeatureTitle>
            <FeatureDescription>
              Use <Highlight>arrow keys</Highlight> to navigate between pick slots.
              <Highlight> Cmd/Ctrl + arrows</Highlight> to jump quickly.
              <Highlight> Shift + arrows</Highlight> to multi-select.
              <Highlight> Cmd/Ctrl + C/V</Highlight> to copy and paste.
            </FeatureDescription>
          </ContentColumn>
          <DemoColumn data-animate="demo">
            <DemoContainer>
              <DraftProvider>
                <DraftBoard
                  scenarioId={8}
                  scenarioName="Try Navigation"
                  picks={Array(20).fill(null)}
                  showButtons={{ addScenario: true, delete: true, duplicate: true, clearAll: true }}
                  isMarketingDemo={true}
                  isDisabledDemo={visibleSection !== 'feature6'}
                  maxScenariosDemo={3}
                />
              </DraftProvider>
            </DemoContainer>
          </DemoColumn>
        </Section>

        {/* Narration 7: GSI Sync */}
        <NarrationSection>
          <VisualIcon>
            <svg viewBox="0 0 200 200" width="180" height="180">
              <circle cx="100" cy="100" r="14" fill="#667eea" />
              <circle cx="100" cy="100" r="45" fill="none" stroke="#667eea" strokeWidth="4" opacity="0.6" />
              <circle cx="100" cy="100" r="75" fill="none" stroke="#667eea" strokeWidth="4" opacity="0.3" />
            </svg>
          </VisualIcon>
          <NarrationQuote>
            What if your draft board just... knew? The moment a hero is picked or banned in your live match.
          </NarrationQuote>
        </NarrationSection>

        {/* Feature 7: Real-Time GSI Sync */}
        <Section data-section-id="feature7">
          <ContentColumn data-animate="content">
            <FeatureTitle>Real-Time GSI Sync</FeatureTitle>
            <FeatureDescription>
              15 hero slots sit above your board and <Highlight>auto-fill</Highlight> as your live Dota match's
              draft happens, powered by <Highlight>Game State Integration</Highlight>. Enemy bans cross out
              automatically — no manual entry once GSI is connected.
            </FeatureDescription>
          </ContentColumn>
          <DemoColumn data-animate="demo">
            <DemoContainer>
              <DraftProvider>
                <GsiSyncDemo isActive={visibleSection === 'feature7'} />
              </DraftProvider>
            </DemoContainer>
          </DemoColumn>
        </Section>

        {/* Narration 8: Lane Brackets & Relations */}
        <NarrationSection>
          <VisualIcon>
            <svg viewBox="0 0 200 200" width="180" height="180">
              <path d="M60 40 Q40 40 40 60 L40 90 Q40 100 30 100 Q40 100 40 110 L40 140 Q40 160 60 160" fill="none" stroke="#667eea" strokeWidth="5" strokeLinecap="round" />
              <path d="M140 40 Q160 40 160 60 L160 90 Q160 100 170 100 Q160 100 160 110 L160 140 Q160 160 140 160" fill="none" stroke="#667eea" strokeWidth="5" strokeLinecap="round" />
            </svg>
          </VisualIcon>
          <NarrationQuote>
            What if you could point at a lane and say "this one" — without typing a single word to your team?
          </NarrationQuote>
        </NarrationSection>

        {/* Feature 8: Lane Brackets & Relations */}
        <Section $reverse data-section-id="feature8">
          <ContentColumn data-animate="content">
            <FeatureTitle>Lane Brackets &amp; Relations</FeatureTitle>
            <FeatureDescription>
              Drag a side gutter to draw a <Highlight>brace</Highlight> around a group of picks, labelling a lane
              for the team. Or draw <Highlight>connector arrows</Highlight> between hero slots to call out
              counters and synergies — built for shot-calling, not just planning alone.
            </FeatureDescription>
          </ContentColumn>
          <DemoColumn data-animate="demo">
            <DemoContainer>
              <DraftProvider>
                <LaneBracketDemo isActive={visibleSection === 'feature8'} />
              </DraftProvider>
            </DemoContainer>
          </DemoColumn>
        </Section>

        {/* Narration 9: Neon Board Highlighting */}
        <NarrationSection>
          <VisualIcon>
            <svg viewBox="0 0 200 200" width="180" height="180">
              <path d="M100 30 L115 80 L165 80 L125 110 L140 160 L100 130 L60 160 L75 110 L35 80 L85 80 Z" fill="none" stroke="#ff00e5" strokeWidth="4" strokeLinejoin="round" />
            </svg>
          </VisualIcon>
          <NarrationQuote>
            What if the board you're leaning toward just... glowed? So everyone scanning the wall sees it instantly.
          </NarrationQuote>
        </NarrationSection>

        {/* Feature 9: Neon Board Highlighting */}
        <Section data-section-id="feature9">
          <ContentColumn data-animate="content">
            <FeatureTitle>Neon Board Highlighting</FeatureTitle>
            <FeatureDescription>
              Click the <Highlight>✨</Highlight> button on any board's toolbar to give it a glowing neon border.
              Click again to remove it. Perfect for flagging the scenario your team is leaning toward when
              you're scrolling through a wall of drafts.
            </FeatureDescription>
          </ContentColumn>
          <DemoColumn data-animate="demo">
            <DemoContainer>
              <DraftProvider>
                <NeonHighlightDemo isActive={visibleSection === 'feature9'} />
              </DraftProvider>
            </DemoContainer>
          </DemoColumn>
        </Section>

        {/* Narration 10: Pull Match Data */}
        <NarrationSection>
          <VisualIcon>
            <svg viewBox="0 0 200 200" width="180" height="180">
              <path d="M50 70 L150 70" stroke="#667eea" strokeWidth="5" strokeLinecap="round" />
              <path d="M130 50 L150 70 L130 90" fill="none" stroke="#667eea" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M150 130 L50 130" stroke="#667eea" strokeWidth="5" strokeLinecap="round" />
              <path d="M70 110 L50 130 L70 150" fill="none" stroke="#667eea" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </VisualIcon>
          <NarrationQuote>
            Game 1 just ended. What if you didn't have to re-type 20 hero picks by hand to plan Game 2?
          </NarrationQuote>
        </NarrationSection>

        {/* Feature 10: Pull Match Data */}
        <Section $reverse data-section-id="feature10">
          <ContentColumn data-animate="content">
            <FeatureTitle>Pull Match Data</FeatureTitle>
            <FeatureDescription>
              Pull a real match's draft straight into a new scenario — each pull gets its own
              <Highlight> color-coded highlight</Highlight>, so Game 1, Game 2, and Game 3 of a Bo3 stay
              visually distinct as you line them up side-by-side to plan the next game.
            </FeatureDescription>
          </ContentColumn>
          <DemoColumn data-animate="demo">
            <DemoContainer>
              <GameColumn>
                <GameLabel>Game 1</GameLabel>
                <DraftProvider>
                  <PullMatchBoardDemo isActive={visibleSection === 'feature10'} startDelay={500} />
                </DraftProvider>
              </GameColumn>
              <GameColumn>
                <GameLabel>Game 2</GameLabel>
                <DraftProvider>
                  <PullMatchBoardDemo isActive={visibleSection === 'feature10'} startDelay={2200} />
                </DraftProvider>
              </GameColumn>
            </DemoContainer>
          </DemoColumn>
        </Section>

      {/* Feature Overview Bento Grid */}
      <BentoSection>
        <BentoHeader>
          <BentoEyebrow>Everything Else</BentoEyebrow>
          <BentoSubtitle>Built for real draft prep, not just a hero picker</BentoSubtitle>
        </BentoHeader>
        <BentoGrid>
          <BentoTile $span={3}>
            <BentoTag>Core</BentoTag>
            <BentoTileTitle>Dual Pick Modes</BentoTileTitle>
            <BentoTileDesc>
              Independent scenario lists for 1st Pick and 2nd Pick — plan both sides of the draft without them ever mixing.
            </BentoTileDesc>
            <MiniBoardRow>
              <BentoBoardClip $zoom={0.3} $height={140}>
                <DraftProvider>
                  <DraftBoard
                    scenarioId={101}
                    scenarioName="1st Pick"
                    picks={Array(20).fill(null)}
                    showButtons={{}}
                    isMarketingDemo={true}
                    isDisabledDemo={true}
                  />
                </DraftProvider>
              </BentoBoardClip>
              <BentoBoardClip $zoom={0.3} $height={140}>
                <DraftProvider>
                  <DraftBoard
                    scenarioId={102}
                    scenarioName="2nd Pick"
                    picks={Array(20).fill(null)}
                    showButtons={{}}
                    isMarketingDemo={true}
                    isDisabledDemo={true}
                  />
                </DraftProvider>
              </BentoBoardClip>
            </MiniBoardRow>
          </BentoTile>

          <BentoTile $span={3}>
            <BentoTag>Practice</BentoTag>
            <BentoTileTitle>Practice Match-up Overlay</BentoTileTitle>
            <BentoTileDesc>
              Full-screen 3-lane practice view with abbreviation-based hero search — drill matchups fast.
            </BentoTileDesc>
          </BentoTile>

          <BentoTile $span={3}>
            <BentoTag>Overview</BentoTag>
            <BentoTileTitle>All Matchups View</BentoTileTitle>
            <BentoTileDesc>
              See every scenario from both pick modes laid out as cards, side-by-side, in one screen.
            </BentoTileDesc>
            <MiniBoardRow>
              <BentoBoardClip $zoom={0.3} $height={140}>
                <DraftProvider>
                  <DraftBoard
                    scenarioId={103}
                    scenarioName="Scenario A"
                    picks={Array(20).fill(null)}
                    showButtons={{}}
                    isMarketingDemo={true}
                    isDisabledDemo={true}
                  />
                </DraftProvider>
              </BentoBoardClip>
              <BentoBoardClip $zoom={0.3} $height={140}>
                <DraftProvider>
                  <DraftBoard
                    scenarioId={104}
                    scenarioName="Scenario B"
                    picks={Array(20).fill(null)}
                    showButtons={{}}
                    isMarketingDemo={true}
                    isDisabledDemo={true}
                  />
                </DraftProvider>
              </BentoBoardClip>
            </MiniBoardRow>
          </BentoTile>

          <BentoTile $span={3}>
            <BentoTag>Data</BentoTag>
            <BentoTileTitle>Import / Export &amp; Cloud Sync</BentoTileTitle>
            <BentoTileDesc>
              Drag-and-drop import/export, offline-first storage, with Firebase sync when signed in. Your drafts, everywhere.
            </BentoTileDesc>
            <BentoVisual>
              <BentoPill $variant="accent">Drag &amp; Drop</BentoPill>
              <BentoPill $variant="accent">Offline-First</BentoPill>
            </BentoVisual>
          </BentoTile>
        </BentoGrid>
      </BentoSection>

      {/* Final CTA */}
      <CTASection>
        <FeatureTitle style={{ textAlign: 'center' }}>Ready to Draft Smarter?</FeatureTitle>
        <FeatureDescription style={{ textAlign: 'center' }}>
          Start planning competitive Dota 2 drafts today - completely free.
        </FeatureDescription>
        <CTAButton onClick={() => navigate('/login')}>
          Get Started Free
        </CTAButton>
      </CTASection>
    </PageContainer>
  );
};

export default MarketingLandingPageV3;
